import ProductPriceMatrixPreviewMutation from '../../graphql/mutations/product/ProductPriceMatrixPreviewMutation.graphql';
import ProductPriceMatrixActivateMutation from '../../graphql/mutations/product/ProductPriceMatrixActivateMutation.graphql';
import ProductPriceMatrixDeactivateMutation from '../../graphql/mutations/product/ProductPriceMatrixDeactivateMutation.graphql';
import ProductPriceMatricesActivateMutation from '../../graphql/mutations/product/ProductPriceMatricesActivateMutation.graphql';
import ProductPriceMatricesDeactivateMutation from '../../graphql/mutations/product/ProductPriceMatricesDeactivateMutation.graphql';
import { InfoButton } from '../../components/connectedForm/Button';

import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getIn from '../../components/connectedForm/utils/getIn';
import ProductPriceMatrixPreview from '../../graphql/entities/product/ProductPriceMatrixPreview';
import getClientMutationId from '../../utils/getClientMutationId';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import {
  AlertType,
  IProductPriceMatricesActivateMutationResult,
  IProductPriceMatricesActivateMutationVariables,
  IProductPriceMatricesDeactivateMutationResult,
  IProductPriceMatricesDeactivateMutationVariables,
} from '../../graphql/schema/p4types';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { useCallback, useContext } from 'react';
import { useActionCriteria } from '../../utils/useActionProps';
import { IDeprecatedFormAction } from '../../@types/entityTypes';
import WorkspaceContext from '../../components/Context/WorkspaceContext';

function queryVariablesFromProps(props) {
  return {
    variables: {
      input: {
        clientMutationId: getClientMutationId(),
        productPriceMatrix: normalizeVariables(props.values || props.formData.data),
      },
    },
  };
}

export const formActions: Array<IDeprecatedFormAction<any>> = [
  {
    description: 'Preview',
    component: InfoButton,
    fn: queryVariablesFromProps,
    individualButton: true,
    mutation: ProductPriceMatrixPreviewMutation,
    mutationWrapper: {
      onCompleteMutation: (data, props) => {
        let job = getIn(data, 'generateProductPriceMatrixPreview.result');
        const path = `${ProductPriceMatrixPreview.workspaceUrl}${job.jobId}/${job.properties.uuid}`;
        window.open(window.location.origin + path, '_blank');
      },
    },
    performTemporarySave: true,
  },
  {
    description: 'Activate',
    component: InfoButton,
    fn: queryVariablesFromProps,
    mutation: ProductPriceMatrixActivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix activated',
      messageSuccess: 'Price matrix has been activated.',
    },
  },
  {
    description: 'Deactivate',
    component: InfoButton,
    fn: queryVariablesFromProps,
    mutation: ProductPriceMatrixDeactivateMutation,
    mutationWrapper: {
      subjectSuccess: 'Price matrix deactivated',
      messageSuccess: 'Price matrix has been deactivated.',
    },
  },
];

enum IProductPriceMatrixWorkspaceAction {
  Activate = 'PRODUCT_PRICE_MATRICES_ACTIVATE',
  Deactivate = 'PRODUCT_PRICE_MATRICES_DEACTIVATE',
}

export const workspaceActionsForPermissions: Array<IWorkspaceAction<IProductPriceMatrixWorkspaceAction>> = [
  { actionReference: IProductPriceMatrixWorkspaceAction.Activate },
  { actionReference: IProductPriceMatrixWorkspaceAction.Deactivate },
];

export const useProductPriceMatrixWorkspaceActions = (): [
  actions: Array<IWorkspaceAction<IProductPriceMatrixWorkspaceAction>>
] => {
  const { dispatchSimpleToast } = useP4ToastController();
  const actionCriteria = useActionCriteria();
  const { reloadData } = useContext(WorkspaceContext);

  console.log({ actionCriteria });

  const [activatePriceMatrices] = useBackgroundMutation<
    IProductPriceMatricesActivateMutationResult,
    IProductPriceMatricesActivateMutationVariables
  >(ProductPriceMatricesActivateMutation, {
    onCompleteJob: () => {
      dispatchSimpleToast('Product Price Matrix Activation', `Price matrices activated.`, AlertType.Success);
      reloadData?.();
    },
  });

  const onClickActivate = useCallback(() => {
    activatePriceMatrices({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          filters: actionCriteria.filters,
          productPriceMatrices: actionCriteria.actionEntities,
          include: actionCriteria.include,
        },
      },
    });
  }, [actionCriteria]);

  const [deactivatePriceMatrices] = useBackgroundMutation<
    IProductPriceMatricesDeactivateMutationResult,
    IProductPriceMatricesDeactivateMutationVariables
  >(ProductPriceMatricesDeactivateMutation, {
    onCompleteJob: () => {
      dispatchSimpleToast('Product Price Matrix Deactivation', `Price matrices deactivated.`, AlertType.Success);
      reloadData?.();
    },
  });

  const onClickDeactivate = useCallback(() => {
    deactivatePriceMatrices({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          filters: actionCriteria.filters,
          productPriceMatrices: actionCriteria.actionEntities,
          include: actionCriteria.include,
        },
      },
    });
  }, [actionCriteria]);

  return [
    [
      {
        key: 'activate',
        text: 'Activate',
        onClick: onClickActivate,
        actionReference: IProductPriceMatrixWorkspaceAction.Activate,
      },
      {
        key: 'deactivate',
        text: 'Deactivate',
        onClick: onClickDeactivate,
        actionReference: IProductPriceMatrixWorkspaceAction.Deactivate,
      },
    ],
  ];
};
